/**
 * GPS Permission Service for FlowRide
 * Manages geolocation permission state and monitoring
 */

/**
 * GPS permission state type
 */
export type GpsPermissionState = "granted" | "denied" | "prompt" | "unknown";

/**
 * Permission state listener callback type
 */
type PermissionStateListener = ( state: GpsPermissionState ) => void;

/**
 * Service that manages GPS permission state
 */
export class GpsPermissionService {

    /** Singleton instance */
    private static instance: GpsPermissionService | null = null;

    /** Permission state listeners */
    private permissionListeners: Set<PermissionStateListener> = new Set();

    /** Current permission state */
    private permissionState: GpsPermissionState = "unknown";

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
        // Private constructor
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): GpsPermissionService {
        if ( GpsPermissionService.instance === null ) {
            GpsPermissionService.instance = new GpsPermissionService();
        }

        return GpsPermissionService.instance;
    }

    /**
     * Check geolocation permission state
     * Only checks if permission is denied to prevent unnecessary watchPosition calls
     * The actual permission request will be triggered by watchPosition() if needed
     * @returns True if permission is granted or can be requested, false if denied
     */
    public async requestPermission(): Promise<boolean> {
        if ( navigator.geolocation === undefined ) {
            console.error( "[GpsPermissionService] Geolocation not supported" );
            this.updatePermissionState( "unknown" );

            return false;
        }

        // Check permission state using Permissions API if available
        if ( typeof navigator.permissions === "object" ) {
            try {
                const result = await navigator.permissions.query( { "name": "geolocation" as PermissionName } );
                const state = result.state as GpsPermissionState;

                this.updatePermissionState( state );

                // Listen for permission changes
                result.addEventListener( "change", () => {
                    const newState = result.state as GpsPermissionState;

                    this.updatePermissionState( newState );
                } );

                if ( state === "denied" ) {
                    console.error( "[GpsPermissionService] Geolocation permission denied" );

                    return false;
                }

                // "granted" or "prompt" - allow watchPosition to proceed
                return true;
            } catch( error ) {
                console.warn( "[GpsPermissionService] Could not check permissions:", error );
                this.updatePermissionState( "unknown" );

                return true;
            }
        }

        // Permissions API not available, assume we can request permission
        this.updatePermissionState( "prompt" );

        return true;
    }

    /**
     * Update permission state and notify listeners
     * @param state - New permission state
     */
    private updatePermissionState( state: GpsPermissionState ): void {
        if ( this.permissionState !== state ) {
            this.permissionState = state;
            this.emitPermissionState( state );
        }
    }

    /**
     * Emit permission state to all listeners
     * @param state - Permission state to emit
     */
    private emitPermissionState( state: GpsPermissionState ): void {
        this.permissionListeners.forEach( ( listener ) => {
            try {
                listener( state );
            } catch( error ) {
                console.error( "[GpsPermissionService] Error in permission listener:", error );
            }
        } );
    }

    /**
     * Get current permission state
     * @returns Current permission state
     */
    public getPermissionState(): GpsPermissionState {
        return this.permissionState;
    }

    /**
     * Check if geolocation permission is granted
     * @returns True if permission is granted
     */
    public isPermissionGranted(): boolean {
        return this.permissionState === "granted";
    }

    /**
     * Subscribe to permission state changes
     * @param listener - Callback function that receives permission state
     * @returns Unsubscribe function
     */
    public subscribeToPermissionState( listener: PermissionStateListener ): () => void {
        // Emit current state immediately
        listener( this.permissionState );

        this.permissionListeners.add( listener );

        return () => {
            this.permissionListeners.delete( listener );
        };
    }

    /**
     * Update permission state (called from external services like RawGpsDataService)
     * @param state - New permission state
     */
    public setPermissionState( state: GpsPermissionState ): void {
        this.updatePermissionState( state );
    }

}

/**
 * Export singleton instance for convenience
 */
export const gpsPermissionService = GpsPermissionService.getInstance();

